import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Divider,
  Badge,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Button,
  CircularProgress,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  Email as EmailIcon,
  Person as PersonIcon,
  Notifications as NotificationsIcon,
  MarkEmailRead as MarkEmailReadIcon,
  AccountCircle as AccountCircleIcon,
  MeetingRoom as MeetingIcon,
  SwitchAccount as SwitchAccountIcon,
} from "@mui/icons-material";
import { useAuth } from "../hooks/useAuth";
import { useNavigate } from "react-router-dom";
import { Profile_page, NOTIFICATIONS_PATH } from "../navigations/path";
import {
  useLazyGetNotificationsQuery,
  useMarkNotificationAsReadMutation,
  useChangePasswordMutation,
} from "../store/api";
import { toast } from "react-toastify";
import { useAppTheme } from "../context/AppThemeContext";
import ThemeSwitcher from "./theme/ThemeSwitcher";
import { getErrorMessage } from "../utils/errorHandler";
import { LockReset as LockResetIcon } from "@mui/icons-material";
import ChangePasswordModal from "./Modals/ChangePasswordModal";

const IMPERSONATION_BANNER_HEIGHT = 40;
const ORANGE_STRIP_HEIGHT = 2;
const HEADER_TOP_IMPERSONATING =
  IMPERSONATION_BANNER_HEIGHT + ORANGE_STRIP_HEIGHT;

const Navbar = () => {
  const [fetchNotifications] = useLazyGetNotificationsQuery();
  const [markNotificationAsRead] = useMarkNotificationAsReadMutation();
  const { user, logout, impersonating, leaveImpersonation } = useAuth();
  const { preset } = useAppTheme();
  const [userAnchorEl, setUserAnchorEl] = useState(null);
  const [leavingImpersonation, setLeavingImpersonation] = useState(false);
  const [notificationAnchorEl, setNotificationAnchorEl] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [totalUnread, setTotalUnread] = useState(0);
  const [, setLoading] = useState(false);
  const [notificationLoading, setNotificationLoading] = useState(false);
  const navigate = useNavigate();

  const [changePasswordOpen, setChangePasswordOpen] = useState(false);

  const [changePassword, { isLoading: isChangingPassword }] =
    useChangePasswordMutation();

  // Fetch notifications on component mount and set up interval
  useEffect(() => {
    fetchUnreadNotifications();

    // const interval = setInterval(() => {
    //   fetchUnreadNotifications();
    // }, 5000);

    // return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional dependency list
  }, []);

  // User menu handlers
  const handleUserMenuOpen = (event) => {
    setUserAnchorEl(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserAnchorEl(null);
  };

  // Notification menu handlers
  const handleNotificationMenuOpen = (event) => {
    setNotificationAnchorEl(event.currentTarget);
    fetchUnreadNotifications(true);
  };

  const handleNotificationMenuClose = () => {
    setNotificationAnchorEl(null);
  };

  // Fetch unread notifications
  const fetchUnreadNotifications = async (isMenuOpen = false) => {
    try {
      if (isMenuOpen) {
        setNotificationLoading(true);
      } else {
        setLoading(true);
      }

      const response = await fetchNotifications().unwrap();
      const data = response?.data ?? response;
      if (data) {
        setNotifications(data?.unread ?? data?.data?.unread ?? []);
        setTotalUnread(data?.total_unread ?? data?.data?.total_unread ?? 0);
      } else {
        setNotifications([]);
        setTotalUnread(0);
      }
    } catch (error) {
      console.error("Error fetching notifications:", error);
      if (isMenuOpen) {
        toast.error(
          error?.data?.message ||
            getErrorMessage(error) ||
            "Failed to load notifications",
        );
      }
      setNotifications([]);
      setTotalUnread(0);
    } finally {
      if (isMenuOpen) {
        setNotificationLoading(false);
      } else {
        setLoading(false);
      }
    }
  };

  const handleLeaveImpersonation = async () => {
    setLeavingImpersonation(true);
    try {
      await leaveImpersonation();
      handleUserMenuClose();
    } catch (error) {
      toast.error(error?.message || "Failed to return to your account");
    } finally {
      setLeavingImpersonation(false);
    }
  };

  const handleLogout = () => {
    logout();
    handleUserMenuClose();
  };

  const handleProfileClick = () => {
    navigate(Profile_page);
    handleUserMenuClose();
  };

  // UPDATED: Mark single notification as read
  const handleMarkAsRead = async (notificationId) => {
    try {
      await markNotificationAsRead(notificationId).unwrap();

      // Remove from local state and update count
      setNotifications((prev) =>
        prev.filter((notif) => notif.id !== notificationId),
      );
      setTotalUnread((prev) => prev - 1);
      toast.success("Notification marked as read");
    } catch (error) {
      console.error("Error marking notification as read:", error);
      toast.error(
        error?.data?.message ||
          getErrorMessage(error) ||
          "Failed to mark notification as read",
      );
    }
  };

  // Get user initials for avatar
  const getUserInitials = () => {
    if (!user?.name) return "U";
    return user.name
      .split(" ")
      .map((word) => word[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  // Format notification time
  const formatTime = (dateString) => {
    if (!dateString) return "Recently";

    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);

    if (diffInHours < 1) {
      const diffInMinutes = Math.floor(diffInHours * 60);
      return diffInMinutes < 1 ? "Just now" : `${diffInMinutes}m ago`;
    } else if (diffInHours < 24) {
      return `${Math.floor(diffInHours)}h ago`;
    } else {
      return `${Math.floor(diffInHours / 24)}d ago`;
    }
  };

  // Get notification icon based on type
  const getNotificationIcon = (notificationType) => {
    switch (notificationType) {
      case "meeting_scheduled":
        return <MeetingIcon sx={{ color: "primary.main", fontSize: 20 }} />;
      default:
        return <EmailIcon sx={{ color: "primary.main", fontSize: 20 }} />;
    }
  };

  // UPDATED: Handle notification click with mark as read and navigation to lead detail
  const handleNotificationClick = async (notification) => {
    try {
      // First mark the notification as read
      await markNotificationAsRead(notification.id).unwrap();

      // Update local state
      setNotifications((prev) =>
        prev.filter((notif) => notif.id !== notification.id),
      );
      setTotalUnread((prev) => prev - 1);

      // Extract UUID from the URL and navigate to lead detail
      if (notification.data?.url) {
        const url = notification.data.url;

        // Check if it's a lead URL and extract UUID
        if (url.includes("/leads/")) {
          // Extract the UUID from the URL
          const urlParts = url.split("/");
          const leadUuid = urlParts[urlParts.length - 1];

          // This matches your route: /leads/view/:id
          navigate(`/leads/view/${leadUuid}`);
        } else if (url.includes("/issues/")) {
          const urlParts = url.split("/");
          const leadUuid = urlParts[urlParts.length - 1];

          navigate(`/issues/view/${leadUuid}`);
        } else {
          // If it's not a lead URL, you can handle other types of notifications here
          // You might want to navigate to a default page or show a message
          // navigate('/dashboard');
        }
      } else {
      }

      handleNotificationMenuClose();
    } catch (error) {
      console.error("Error handling notification click:", error);
      toast.error("Failed to process notification");
    }
  };

  const headerTop = impersonating ? HEADER_TOP_IMPERSONATING : 0;

  const handleChangePassword = async (data) => {
    try {
      await changePassword({
        _method: "PUT",
        ...data,
      }).unwrap();

      toast.success("Password changed successfully");
    } catch (error) {
      toast.error(
        error?.data?.message ||
          getErrorMessage(error) ||
          "Failed to change password",
      );

      throw error;
    }
  };

  return (
    <Box>
      {impersonating && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            height: IMPERSONATION_BANNER_HEIGHT,
            zIndex: 1101,
            bgcolor: "#fff3e0",
            borderBottom: 1,
            borderColor: "#ffb74d",
            px: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Typography
            variant="body2"
            sx={{ fontWeight: 600, color: "#e65100" }}
          >
            Viewing as {user?.name || "user"} (impersonation)
          </Typography>
          <Button
            size="small"
            variant="contained"
            color="warning"
            disabled={leavingImpersonation}
            onClick={handleLeaveImpersonation}
            startIcon={
              leavingImpersonation ? (
                <CircularProgress size={16} color="inherit" />
              ) : null
            }
          >
            Back to my account
          </Button>
        </Box>
      )}

      {/* Top Orange Strip */}
      <Box
        sx={{
          height: ORANGE_STRIP_HEIGHT,
          bgcolor: preset.accent,
          width: "100%",
          position: "fixed",
          top: impersonating ? IMPERSONATION_BANNER_HEIGHT : 0,
          left: 0,
          right: 0,
          zIndex: 1100,
        }}
      />

      {/* Main Header */}
      <Box
        sx={{
          bgcolor: "white",
          borderBottom: 1,
          borderColor: "grey.200",
          px: 3,
          py: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          position: "fixed",
          top: headerTop,
          left: 0,
          right: 0,
          zIndex: 1000,
        }}
      >
        {/* Logo — intrinsic width + left align (avoid width:100% stretching across flex:1 when sidebar opens) */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flex: 1,
            justifyContent: "flex-start",
            minWidth: 0,
          }}
        >
          <Box
            component="a"
            href="/"
            sx={{
              display: "flex",
              alignItems: "center",
              flexShrink: 0,
              lineHeight: 0,
            }}
          >
            <Box
              component="img"
              src="/images/revivebussiness-loogo.png"
              alt="Revive Portal"
              sx={{
                height: { xs: 48, sm: 56 },
                width: "auto",
                maxWidth: { xs: 180, sm: 220 },
                display: "block",
                objectFit: "contain",
              }}
            />
          </Box>
        </Box>

        {/* Right Side - Notifications and User Info */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <ThemeSwitcher />

          {/* Notifications Bell Icon */}
          <IconButton
            onClick={handleNotificationMenuOpen}
            sx={{
              color: "primary.main",
              "&:hover": {
                backgroundColor: (theme) =>
                  alpha(theme.palette.primary.main, 0.08),
              },
            }}
          >
            <Badge badgeContent={totalUnread} color="error" max={99}>
              <NotificationsIcon />
            </Badge>
          </IconButton>

          {/* User Info with Avatar and Name */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              cursor: "pointer",
              padding: "8px 12px",
              borderRadius: 1,
              "&:hover": {
                backgroundColor: (theme) =>
                  alpha(theme.palette.primary.main, 0.08),
              },
            }}
            onClick={handleUserMenuOpen}
          >
            <Avatar
              sx={{
                width: 32,
                height: 32,
                bgcolor: "primary.main",
                fontSize: "0.875rem",
                fontWeight: 600,
              }}
            >
              {getUserInitials()}
            </Avatar>
            <Typography
              variant="body2"
              sx={{
                color: "primary.main",
                fontWeight: 500,
                display: { xs: "none", sm: "block" },
              }}
            >
              {user?.name || "User"}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: "primary.main",
                fontWeight: 500,
                display: { xs: "none", sm: "block" },
              }}
            >
              ( {user?.roles?.[0]?.name})
            </Typography>
          </Box>
        </Box>

        {/* Notifications Dropdown Menu */}
        <Menu
          anchorEl={notificationAnchorEl}
          open={Boolean(notificationAnchorEl)}
          onClose={handleNotificationMenuClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          PaperProps={{
            sx: {
              width: 360,
              maxHeight: 480,
              borderRadius: 2,
              boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
              mt: 1,
            },
          }}
        >
          {/* Notifications Header */}
          <Box sx={{ p: 2, borderBottom: 1, borderColor: "grey.200" }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography
                variant="h6"
                sx={{ color: "primary.main", fontWeight: 600 }}
              >
                Notifications
                {totalUnread > 0 && (
                  <Typography
                    component="span"
                    sx={{
                      ml: 1,
                      color: "error.main",
                      fontSize: "0.75rem",
                      fontWeight: 600,
                    }}
                  >
                    ({totalUnread} unread)
                  </Typography>
                )}
              </Typography>
              {notifications.length > 0 && (
                <Button
                  size="small"
                  startIcon={<MarkEmailReadIcon />}
                  // onClick={handleMarkAllAsRead}
                  sx={{
                    color: "primary.main",
                    fontSize: "0.75rem",
                    textTransform: "none",
                  }}
                >
                  Mark all as read
                </Button>
              )}
            </Box>
          </Box>

          {/* Notifications List */}
          <Box sx={{ maxHeight: 300, overflow: "auto" }}>
            {notificationLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                <CircularProgress size={24} sx={{ color: "primary.main" }} />
              </Box>
            ) : notifications.length === 0 ? (
              <Box sx={{ p: 3, textAlign: "center" }}>
                <NotificationsIcon
                  sx={{ fontSize: 48, color: "grey.400", mb: 1 }}
                />
                <Typography variant="body2" color="text.secondary">
                  No new notifications
                </Typography>
              </Box>
            ) : (
              <List sx={{ p: 0 }}>
                {notifications.map((notification) => (
                  <ListItem
                    key={notification.id}
                    sx={{
                      borderBottom: 1,
                      borderColor: "grey.100",
                      "&:last-child": { borderBottom: 0 },
                      "&:hover": {
                        backgroundColor: "grey.50",
                        cursor: "pointer",
                      },
                    }}
                    onClick={() => handleNotificationClick(notification)}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      {getNotificationIcon(notification.data?.type)}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {notification.data?.title || "New Notification"}
                        </Typography>
                      }
                      secondary={
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            {notification.data?.message ||
                              "You have a new notification"}
                          </Typography>
                          <Typography
                            variant="caption"
                            display="block"
                            color="text.secondary"
                            sx={{ mt: 0.5 }}
                          >
                            {formatTime(notification.created_at)}
                          </Typography>
                        </Box>
                      }
                    />
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMarkAsRead(notification.id);
                      }}
                      sx={{ color: "grey.500" }}
                    >
                      <MarkEmailReadIcon fontSize="small" />
                    </IconButton>
                  </ListItem>
                ))}
              </List>
            )}
          </Box>

          {/* Notifications Footer */}
          <Box
            sx={{
              p: 1,
              borderTop: 1,
              borderColor: "grey.200",
              textAlign: "center",
            }}
          >
            <Button
              size="small"
              onClick={() => {
                handleNotificationMenuClose();
                navigate(NOTIFICATIONS_PATH);
              }}
              sx={{
                color: "primary.main",
                fontSize: "0.75rem",
                textTransform: "none",
              }}
            >
              View all notifications
            </Button>
          </Box>
        </Menu>

        {/* User Dropdown Menu */}
        <Menu
          anchorEl={userAnchorEl}
          open={Boolean(userAnchorEl)}
          onClose={handleUserMenuClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          PaperProps={{
            sx: {
              width: 200,
              borderRadius: 2,
              boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
              mt: 1,
            },
          }}
        >
          {/* User Info in Dropdown */}
          <Box sx={{ p: 2, borderBottom: 1, borderColor: "grey.200" }}>
            <Typography
              variant="subtitle2"
              sx={{ color: "primary.main", fontWeight: 600 }}
            >
              {user?.name || "User"}
            </Typography>
            <Typography
              variant="subtitle2"
              sx={{ color: "primary.main", fontWeight: 400 }}
            >
              ({user?.roles?.[0]?.name || "No Role"})
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {user?.email || "user@example.com"}
            </Typography>
          </Box>

          <MenuItem onClick={handleProfileClick}>
            <ListItemIcon>
              <AccountCircleIcon
                fontSize="small"
                sx={{ color: "primary.main" }}
              />
            </ListItemIcon>
            <Typography variant="body2">My Profile</Typography>
          </MenuItem>

          <MenuItem
            onClick={() => {
              handleUserMenuClose();
              setChangePasswordOpen(true);
            }}
          >
            <ListItemIcon>
              <LockResetIcon fontSize="small" sx={{ color: "primary.main" }} />
            </ListItemIcon>
            <Typography variant="body2">Change Password</Typography>
          </MenuItem>

          {impersonating && (
            <MenuItem
              onClick={handleLeaveImpersonation}
              disabled={leavingImpersonation}
            >
              <ListItemIcon>
                <SwitchAccountIcon fontSize="small" sx={{ color: "#e65100" }} />
              </ListItemIcon>
              <Typography variant="body2" sx={{ color: "#e65100" }}>
                Back to my account
              </Typography>
            </MenuItem>
          )}

          <Divider />

          <MenuItem onClick={handleLogout}>
            <ListItemIcon>
              <PersonIcon fontSize="small" sx={{ color: "error.main" }} />
            </ListItemIcon>
            <Typography variant="body2" color="error">
              Logout
            </Typography>
          </MenuItem>
        </Menu>
      </Box>
      <ChangePasswordModal
        open={changePasswordOpen}
        onClose={() => setChangePasswordOpen(false)}
        onSubmit={handleChangePassword}
        isLoading={isChangingPassword}
      />
    </Box>
  );
};

export default Navbar;
